import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Leaf, CalendarDays, Lightbulb, ExternalLink, Zap, FlaskConical, Lock, Crown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APPROACH_CONFIG = {
  herbal: { label: "Herbal & Ayurvedic", icon: <Leaf className="w-4 h-4" />, color: "text-[#4ADE80]", bg: "bg-[#4ADE80]/15" },
  adaptogen: { label: "Adaptogens", icon: <Zap className="w-4 h-4" />, color: "text-[#FF6B4A]", bg: "bg-[#FF6B4A]/10" },
  allopathic: { label: "Clinical & Supplements", icon: <FlaskConical className="w-4 h-4" />, color: "text-[#A388EE]", bg: "bg-[#A388EE]/10" },
};

const ProductRow = ({ product, onAffilClick }) => (
  <div className="flex gap-3 items-center p-3 bg-[#FFF2EC] rounded-xl hover:shadow-md transition-all duration-300 group">
    <img src={product.image} alt={product.name} className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <h4 className="font-semibold text-[#1A1514] text-sm truncate">{product.name}</h4>
      <p className="text-xs text-[#8C7A76] line-clamp-1">{product.brand} — {product.description}</p>
      <div className="flex items-center justify-between mt-1.5">
        <span className="font-bold text-[#FF6B4A] text-sm">{product.price}</span>
        <a
          href={product.affiliate_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onAffilClick(product)}
          className="inline-flex items-center gap-1 bg-[#A388EE] hover:bg-[#b5664a] text-white rounded-full px-3 text-xs h-7 font-medium transition-colors cursor-pointer"
          data-testid={`buy-${product.product_id}`}
        >
          View on Amazon <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  </div>
);

const ProductGroup = ({ approachKey, products, onAffilClick }) => {
  const config = APPROACH_CONFIG[approachKey] || APPROACH_CONFIG.allopathic;
  if (!products || products.length === 0) return null;

  return (
    <div>
      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${config.bg} ${config.color} text-xs font-semibold mb-3`}>
        {config.icon} {config.label}
      </div>
      <div className="space-y-3">
        {products.map((product) => (
          <ProductRow key={product.product_id} product={product} onAffilClick={onAffilClick} />
        ))}
      </div>
    </div>
  );
};

const LockedProductsPreview = ({ onUpgrade }) => (
  <Card className="lg:col-span-8 card-elevated border-0 relative overflow-hidden">
    <CardHeader>
      <span className="overline text-[#A388EE]">Holistic Approach</span>
      <CardTitle className="text-[#1A1514]">Products For You</CardTitle>
      <p className="text-sm text-[#8C7A76]">Herbal, adaptogenic & clinical recommendations based on your analysis</p>
    </CardHeader>
    <CardContent>
      {/* Blurred preview */}
      <div className="relative">
        <div className="space-y-3 filter blur-[6px] pointer-events-none select-none" aria-hidden="true">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3 items-center p-3 bg-[#FFF2EC] rounded-xl">
              <div className="w-14 h-14 bg-[#E8E6E3] rounded-lg flex-shrink-0" />
              <div className="flex-1">
                <div className="h-4 bg-[#E8E6E3] rounded w-3/4 mb-2" />
                <div className="h-3 bg-[#E8E6E3] rounded w-1/2 mb-2" />
                <div className="flex justify-between">
                  <div className="h-4 bg-[#E8E6E3] rounded w-16" />
                  <div className="h-7 bg-[#A388EE]/30 rounded-full w-28" />
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Overlay CTA */}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[2px] rounded-xl">
          <div className="bg-white shadow-lg rounded-2xl p-6 text-center max-w-sm border border-[#E8E6E3]">
            <div className="w-12 h-12 bg-[#FF6B4A]/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Lock className="w-6 h-6 text-[#FF6B4A]" />
            </div>
            <h3 className="font-bold text-[#1A1514] text-lg mb-1">Unlock Personalized Products</h3>
            <p className="text-sm text-[#8C7A76] mb-4">
              Get curated herbal, adaptogenic & clinical product recommendations tailored to your skin triggers.
            </p>
            <button
              onClick={onUpgrade}
              className="inline-flex items-center gap-2 bg-[#FF6B4A] hover:bg-[#E8552F] text-white rounded-full px-6 py-2.5 text-sm font-semibold transition-colors cursor-pointer"
              data-testid="unlock-products-btn"
            >
              <Crown className="w-4 h-4" /> Upgrade to Premium
            </button>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const RecommendationsSection = ({ recommendations, subscription, onUpgrade }) => {
  const isPremium = subscription === 'premium';
  const handleAffiliateClick = (product) => {
    // Fire-and-forget: track the click in background, link opens naturally via <a> tag
    axios.post(`${API}/affiliate/click`, {
      product_id: product.product_id
    }, { withCredentials: true }).catch(() => {});
  };

  // Support both old flat array and new grouped format
  const products = recommendations?.products || {};
  const isGrouped = !Array.isArray(products);
  const herbalProducts = isGrouped ? (products.herbal || []) : [];
  const allopathicProducts = isGrouped ? (products.allopathic || []) : [];
  const adaptogenProducts = isGrouped ? (products.adaptogen || []) : [];
  const flatProducts = Array.isArray(products) ? products : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Supplements Advice */}
      <Card className="lg:col-span-4 card-elevated border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1A1514]">
            <Leaf className="w-5 h-5 text-[#4ADE80]" />
            Supplement Advice
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recommendations?.supplements?.map((supp) => (
              <div key={supp.name} className="rec-card">
                <div className="flex gap-3">
                  {supp.affiliate?.image && (
                    <a href={supp.affiliate.affiliate_url} target="_blank" rel="noopener noreferrer" onClick={() => handleAffiliateClick(supp.affiliate)} className="flex-shrink-0">
                      <img src={supp.affiliate.image} alt={supp.affiliate.name} className="w-16 h-16 object-contain rounded-lg bg-white border border-[#E5D0C9] p-1" />
                    </a>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-[#1A1514]">{supp.name}</h4>
                        <p className="text-xs text-[#8C7A76] mt-0.5">{supp.dosage}</p>
                      </div>
                      <span className={`tag text-xs ${supp.priority === 'high' ? 'bg-[#A388EE]/20 text-[#A388EE]' : ''}`}>
                        {supp.priority}
                      </span>
                    </div>
                    <p className="text-sm text-[#8C7A76] mt-1.5">{supp.reason}</p>
                    {supp.affiliate && (
                      <div className="flex items-center gap-2 mt-2">
                        <a
                          href={supp.affiliate.affiliate_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleAffiliateClick(supp.affiliate)}
                          className="inline-flex items-center gap-1 bg-[#A388EE] hover:bg-[#b5664a] text-white rounded-full px-3 text-xs h-7 font-medium transition-colors"
                          data-testid={`buy-supp-${supp.name.replace(/\s/g, '-').toLowerCase()}`}
                        >
                          {supp.affiliate.price} — Buy on Amazon <ExternalLink className="w-3 h-3" />
                        </a>
                        <span className="text-[10px] text-[#8C7A76]">{supp.affiliate.brand}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {(!recommendations?.supplements || recommendations.supplements.length === 0) && (
              <p className="text-[#8C7A76] text-center py-4">Log more data to get supplement recommendations.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Holistic Products - Locked for free users */}
      {isPremium ? (
        <Card className="lg:col-span-8 card-elevated border-0">
          <CardHeader>
            <span className="overline text-[#A388EE]">Holistic Approach</span>
            <CardTitle className="text-[#1A1514]">Products For You</CardTitle>
            <p className="text-sm text-[#8C7A76]">Herbal, adaptogenic & clinical recommendations based on your analysis</p>
          </CardHeader>
          <CardContent>
            {isGrouped ? (
              <div className="space-y-6">
                <ProductGroup approachKey="herbal" products={herbalProducts} onAffilClick={handleAffiliateClick} />
                <ProductGroup approachKey="adaptogen" products={adaptogenProducts} onAffilClick={handleAffiliateClick} />
                <ProductGroup approachKey="allopathic" products={allopathicProducts} onAffilClick={handleAffiliateClick} />
              </div>
            ) : (
              <div className="space-y-3">
                {flatProducts.map((product) => (
                  <ProductRow key={product.product_id} product={product} onAffilClick={handleAffiliateClick} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <LockedProductsPreview onUpgrade={onUpgrade} />
      )}

      {/* Routines */}
      <Card className="lg:col-span-6 card-elevated border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1A1514]">
            <CalendarDays className="w-5 h-5 text-[#FF6B4A]" />
            Recommended Routines
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recommendations?.routines?.map((routine) => (
              <div key={routine.name} className="rec-card">
                <div className="flex justify-between items-start">
                  <h4 className="font-semibold text-[#1A1514]">{routine.name}</h4>
                  <span className="text-xs text-[#8C7A76]">{routine.schedule}</span>
                </div>
                <p className="text-sm text-[#8C7A76] mt-2">{routine.tip}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Insights */}
      <Card className="lg:col-span-6 card-elevated border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1A1514]">
            <Lightbulb className="w-5 h-5 text-[#4ADE80]" />
            Personalized Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recommendations?.insights?.map((insight) => (
              <div key={insight} className="p-4 bg-[#FFF2EC] rounded-xl">
                <p className="text-sm text-[#1A1514]">{insight}</p>
              </div>
            ))}
            {(!recommendations?.insights || recommendations.insights.length === 0) && (
              <p className="text-[#8C7A76] text-center py-4">Log more data to get personalized insights.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RecommendationsSection;
